import {Reel} from 'com/gongxigames/math/core/game/Reel';
import {Container, Graphics} from 'pixi.js';
import {CoreDI, inject} from '@gongxigames/framework/core';
import {AbstractReelsView} from '@gongxigames/framework/slot/modules/reels/AbstractReelsView';
import {ReelSymbol} from '@gongxigames/framework/slot/modules/reels/ReelSymbol';
import {ReelView, ReelViewData} from '@gongxigames/framework/slot/modules/reels/ReelView';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {GameConfig} from '@gongxigames/framework/slot/types';
import {ReelConstants} from '@gongxigames/framework/slot/utils';
import {ReelsViewEvents} from '@gongxigames/framework/slot/modules/reels/ReelsViewEvents';

export abstract class ReelsView extends AbstractReelsView {
    public static readonly NAME: symbol = Symbol('ReelsView');
    public static layer = LAYERS.REELS;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    protected _currentMask?: Graphics;

    protected _reelsLayerToBeMasked!: Container;

    protected _scatterHitOnReel = -1;

    protected _animationToBeMaskedContainer!: Container;

    protected onStopReel(reelIndex: number): void {
        this._reels[reelIndex].scatterOnReelThatTriggerFreeGames = this._scattersOnReels[reelIndex];

        super.onStopReel(reelIndex);
    }

    public reOrder(): void {
        this.defaultSymbolsOrder.forEach((order) => {
            for (const reel of this._reels) {
                !reel.isSpinning && reel.iterateRows((rowIndex) => {
                    const symbol = reel.reelSymbols[this.rowIndexToSymbolIndex(rowIndex)];
                    const rowSymbolType = symbol.symbolType;

                    if (this.isSymbolsEqual(order, rowSymbolType)) {
                        reel.setSymbolOnTop(symbol);
                    }
                });
            }
        });
    }

    protected removeOversizedsFromSpecialLayer(): void {
        // It is necessary to sort the characters in order not to insert in non-existent indexes
        this._oversizedsOnStoppedReelsLayer.children.sort((child1, child2) => {
            return (child1 as ReelSymbol).reelSetIndex - (child2 as ReelSymbol).reelSetIndex;
        });

        while (this._oversizedsOnStoppedReelsLayer.children.length > 0) {
            const symbol = (this._oversizedsOnStoppedReelsLayer.getChildAt(0) as ReelSymbol);

            this._reelsetContainer.addChildAt(symbol, symbol.reelSetIndex);
        }

        // Move background container to the bottom
        this._reelsetContainer.children.forEach((child) => {
            if (!(child instanceof ReelSymbol)) {
                this._reelsetContainer.addChildAt(child, 0);
            }
        });
    }

    protected rowIndexToSymbolIndex(rowIndex: number): number {
        return rowIndex + Math.abs(ReelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET);
    }

    public setReelsBackground(): void {
        for (const reel of this._reels) {
            if (this.isBaseGame) {
                reel.setMainGameBackground();
            } else {
                reel.setFreeGamesBackground();
            }
        }
    }

    protected createReelSet(reels: Reel[], reelsPosition: number[]): void {
        this._symbolsPool.freeAll();
        this.setupReelsLayers();

        this.iterateReels(reels.length, (reelIndex) => {
            const reelData = this.getReelData(reelIndex);

            if (reelsPosition && reelsPosition[reelIndex]) {
                reelData.startPosition = reelsPosition[reelIndex];
            }

            const reel: ReelView = this.createReel(reelData);

            reel.strip = reels[reelIndex].getSymbols();
            reel.init(this.objectBuilder, this.spineUtils);
            reel.onReelStopping = this.onReelStopping.bind(this);
            reel.onReelStopped = this.onReelStopped.bind(this);
            reel.onReelAnimationFinished = this.onReelAnimationFinished.bind(this);
            reel.onScatterHitAnimationCallback = this.onScatterHitAnimation.bind(this, reelIndex);
            reel.onBounceStoppingStartedCallback = this.onBounceStoppingStarted.bind(this, reelIndex);

            this.updateManager.add(reel);
            reel.start();
            this._reels[reelIndex] = reel;
        });

        this.setupReelSetMaskForStatic();
        this.setReelsBackground();
        this.forceUpdateOrientation();
        this.reorderAllOversizeds();
    }

    /**
     * This function setups default reels layers
     * Should be overridden if need to add additional layers
     */
    protected setupReelsLayers(): void {
        for (const reel of this._reels) {
            this.updateManager.remove(reel);
        }

        this._reels = [];
        this.container.removeChildren();

        this._reelsetContainer = new Container();
        this._reelSetAnimationContainer = new Container();
        this._oversizedsOnStoppedReelsLayer = new Container();
        this._reelsLayerToBeMasked = new Container();
        this._animationToBeMaskedContainer = new Container();

        this.container.addChild(this._reelsLayerToBeMasked);
        this.container.addChild(this._oversizedsOnStoppedReelsLayer);

        this._reelsLayerToBeMasked.addChild(this._reelsetContainer);
        this._reelsLayerToBeMasked.addChild(this._animationToBeMaskedContainer);
        this.container.addChild(this._reelSetAnimationContainer);
    }

    /**
     * This function returns default reel data
     * Should be overridden if need to add additional reel data
     */
    protected getReelData(reelIndex: number): ReelViewData {
        return {
            index: reelIndex,
            reelSpeed: this.reelConstants.REEL_SPEED_MULTIPLIER,
            startPosition: 0,
            reelsContainer: this._reelsetContainer,
            animationContainer: this._reelSetAnimationContainer,
            animationToBeMasked: this._animationToBeMaskedContainer,
            offsets: {
                x: this.reelConstants.REELS[reelIndex].x,
                y: this.reelConstants.REELS[reelIndex].y
            },
            reelsConfig: this._gameConfig.reelsConfig,
            symbolsPool: this._symbolsPool,
            reelConstants: this.reelConstants
        } as ReelViewData;
    }

    public reorderAllOversizeds(): void {
    }

    protected onReelStopping(reelNr: number, distanceBeforeStop: number): void {
        const isLastReel = reelNr === ReelConstants.REELS_AMOUNT - 1;

        this.playReelRelatedSound(reelNr);

        if (isLastReel) {
            this._animationToBeMaskedContainer.removeChildren();
            this.isQuickStop && this.playLastReelStopSound();
        }

        this.emit(ReelsViewEvents.REEL_STOPPING_CALLBACK, reelNr, distanceBeforeStop);
    }

    protected onReelStopped(reelNr: number): void {
        if (reelNr === 0) {
            if (!this._reels[this._reels.length - 1].isStopped) {
                this.setupReelSetMaskForFirstReelStopped();
            } else {
                this.setupReelSetMaskForStatic();
            }
        }

        this.reOrder();
        this.putOversizedsOnSpecialLayer(reelNr);
        this.emit(ReelsViewEvents.REEL_STOPPED, reelNr);

        const allReelsStopped = this._reels.every((reel) => {
            return reel.isStopped;
        });

        if (allReelsStopped) {
            this.onLastReelStopped();
        }
    }

    protected onReelAnimationFinished(reelNr: number): void {
        this.reOrder();

        const allReelsAnimationsStopped = this._reels.every((reel) => {
            return reel.isHitAnimationsFinished;
        });

        if (allReelsAnimationsStopped) {
            this.onLastReelAnimationFinished();
        }
    }

    protected onScatterHitAnimation(reelNr: number): void {
        this._scatterHitOnReel = reelNr;
        this.scattersNumber++;
        this.emit(ReelsViewEvents.SCATTER_HIT_ANIMATION_PLAYING);
    }

    protected onBounceStoppingStarted(reelNr: number): void {
    }

    protected onLastReelStopped(): void {
        this.setupReelSetMaskForStatic();
        this.emit(ReelsViewEvents.ALL_REELS_STOPPED);

        this._scatterHitOnReel = -1;
    }

    protected playReelRelatedSound(reelNr: number): void {
        if (this._scatterHitOnReel !== reelNr && !this._isQuickStop) {
            this.soundPlayer.play('reelstop' + (reelNr + 1));
        }
    }

    protected playLastReelStopSound(): void {

        if (this.scattersNumber === 1) {
            this.soundPlayer.play('scatterhit1');
        }

        if (this.scattersNumber === 2) {
            this.soundPlayer.play('quickstop2scatter');
        }

        if (this.scattersNumber >= 3) {
            this.soundPlayer.play('quickstop3ormorescatter');
        }

        if (this.scattersNumber < 1) {
            this.soundPlayer.play('reelstop' + this._reelConstants.REELS_AMOUNT);
        }

        this.scattersNumber = 0;
    }

    protected getNumberOfScattersOnReels(): number {
        let scattersNumber = 0;

        for (let reelIndex = 0; reelIndex < this._reelConstants.REELS_AMOUNT; reelIndex++) {
            for (let rowIndex = 0; rowIndex < this._reelConstants.ROW_AMOUNT; rowIndex++) {
                const stripIndex = this.makeNormalizedPosition(this._stopPositions[reelIndex] + rowIndex, this._reels[reelIndex].strip);
                const symbol = this._reels[reelIndex].strip[stripIndex];

                if (symbol === this.scatterIndex) {
                    scattersNumber++;
                }
            }
        }

        return scattersNumber;
    }

    protected makeNormalizedPosition(position: number, strip: number[]): number {
        if (position < 0) {
            return (position % strip.length) + strip.length;
        }

        return position % strip.length;
    }

    protected setupReelSetMaskForSpinning(): void {
        if (this._currentMask && this._reelsLayerToBeMasked.children.indexOf(this._currentMask) >= 0) {
            this._reelsLayerToBeMasked.removeChild(this._currentMask);
        }
        this._currentMask = new Graphics();
        const symbol = this._reelConstants.SYMBOL_SLOT;
        const borderWidth = symbol.width - this._reelConstants.SYMBOL.width;
        const borderHeight = symbol.height - this._reelConstants.SYMBOL.height;

        this._currentMask.beginFill(0xffffff);
        this._currentMask.drawRect(0, 0, 1, 1);
        this._currentMask.endFill();

        this._currentMask.x = this._reelConstants.REELS_OFFSETS.x;
        this._currentMask.y = this._reelConstants.REELS_OFFSETS.y;
        this._currentMask.width = symbol.width * this._reelConstants.REELS_AMOUNT - borderWidth;
        this._currentMask.height = symbol.height * this._reelConstants.ROW_AMOUNT - borderHeight;

        this._reelsLayerToBeMasked.addChild(this._currentMask);

        this._reelsLayerToBeMasked.mask = this._currentMask;
    }

    protected setupReelSetMaskForFirstReelStopped(): void {
        if (this._currentMask && this._reelsLayerToBeMasked.children.indexOf(this._currentMask) >= 0) {
            this._reelsLayerToBeMasked.removeChild(this._currentMask);
        }
        this._currentMask = new Graphics();
        const symbol = this._reelConstants.SYMBOL_SLOT;
        const borderWidth = symbol.width - this._reelConstants.SYMBOL.width;
        const borderHeight = symbol.height - this._reelConstants.SYMBOL.height;

        this._currentMask.beginFill(0xffffff);
        this._currentMask.drawRect(0, 0, 1, 1);
        this._currentMask.endFill();

        this._currentMask.x = 0;
        this._currentMask.y = this._reelConstants.REELS_OFFSETS.y;
        this._currentMask.width = this._reelConstants.REELS_OFFSETS.x + symbol.width * this._reelConstants.REELS_AMOUNT - borderWidth;
        this._currentMask.height = symbol.height * this._reelConstants.ROW_AMOUNT - borderHeight;

        this._reelsLayerToBeMasked.addChild(this._currentMask);

        this._reelsLayerToBeMasked.mask = this._currentMask;
    }

    protected setupReelSetMaskForStatic(): void {
        if (this._currentMask && this._reelsLayerToBeMasked.children.indexOf(this._currentMask) >= 0) {
            this._reelsLayerToBeMasked.removeChild(this._currentMask);
        }
        this._currentMask = new Graphics();
        const symbol = this._reelConstants.SYMBOL_SLOT;

        this._currentMask.beginFill(0xffffff);
        this._currentMask.drawRect(0, 0, 1, 1);
        this._currentMask.endFill();

        this._currentMask.x = 0;
        this._currentMask.y = this._reelConstants.REELS_OFFSETS.y;
        this._currentMask.width = this._gameConfig.viewport.desktop.width;
        this._currentMask.height = symbol.height * this._reelConstants.ROW_AMOUNT;

        this._reelsLayerToBeMasked.addChild(this._currentMask);

        this._reelsLayerToBeMasked.mask = this._currentMask;
    }

    public onOrientationChanged(): void {
        this._reelsScaleService.scaleByCurrentScreenOrientation(
            this._reelsLayerToBeMasked,
            this._oversizedsOnStoppedReelsLayer,
            this._reelSetAnimationContainer
        );
    }

    protected abstract createReel(reelData: ReelViewData): ReelView;
}
